import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";
import { useSnackbar } from "notistack";
import { downloadDropboxFile } from "../../../../../redux/slices/dropboxFoldersSlice";

export const PDFViewer = ({ url, data, isPreview = false, selectedFile }: any) => {
  const [pdf, setPdf] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [renderedPages, setRenderedPages] = useState<any[]>([]);
  const [renderingPages, setRenderingPages] = useState(false);

  const { downloadLoading } = useAppSelector((state) => state.dropboxFolders);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const currentDownloadLoading = selectedFile?.path
    ? downloadLoading[selectedFile.path]
    : false;

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Import PDF.js from local files in src directory
        const pdfjsLib = await import("../../../../../libs/pdfjs/pdf.mjs");

        // Set worker path to local worker file
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "../../../../../libs/pdfjs/pdf.worker.mjs",
          import.meta.url
        ).href;

        let loadingTask;

        if (data) {
          // Handle raw data (base64 string or Uint8Array)
          if (typeof data === "string") {
            // Convert base64 to Uint8Array
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            loadingTask = pdfjsLib.getDocument({ data: bytes });
          } else if (data instanceof Uint8Array) {
            // Direct Uint8Array
            loadingTask = pdfjsLib.getDocument({ data });
          } else {
            throw new Error("Invalid data format");
          }
        } else if (url) {
          // Handle URL
          loadingTask = pdfjsLib.getDocument(url);
        } else {
          throw new Error("Either url or data must be provided");
        }

        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
      } catch (err) {
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF");
      } finally {
        setLoading(false);
      }
    };

    if (url || data) {
      loadPDF();
    }
  }, [url, data]);

  useEffect(() => {
    const renderAllPages = async () => {
      if (!pdf) return;

      try {
        setRenderingPages(true);
        const pages = [];

        // Render all pages at original size (scale 1.0) for best quality
        const scale = 1.0;

        // Render all pages
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          // Create canvas for this page
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          // Set canvas dimensions
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // Render page to canvas
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };

          await page.render(renderContext).promise;

          pages.push({
            pageNum,
            canvas,
            width: viewport.width,
            height: viewport.height,
          });
        }

        setRenderedPages(pages);
      } catch (err) {
        console.error("Error rendering pages:", err);
        setError("Failed to render PDF pages");
      } finally {
        setRenderingPages(false);
      }
    };

    renderAllPages();
  }, [pdf, totalPages]);

  const handleDownload = async () => {
    // For preview files (non-PDF files that are previewed as PDF), use download action
    if (isPreview && selectedFile?.path) {
      try {
        const result = await dispatch(
          downloadDropboxFile({
            filePath: selectedFile.path,
          })
        ).unwrap();

        // The temporary link is in the 'link' property of the result
        const downloadUrl = result?.link;
        if (downloadUrl) {
          // Use fetch to download the file as blob to ensure proper handling
          const response = await fetch(downloadUrl);
          const blob = await response.blob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = selectedFile.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Failed to get temporary link for download:", error);
        enqueueSnackbar("Failed to download file", { variant: "error" });
      }
      return;
    }

    // For actual PDF files, use existing logic
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = selectedFile?.name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (data) {
      // Create blob from raw data
      let blob;
      if (typeof data === "string") {
        // Convert base64 to blob
        const binaryString = atob(data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: "application/pdf" });
      } else if (data instanceof Uint8Array) {
        blob = new Blob([data as unknown as BlobPart], { type: "application/pdf" });
      }

      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = selectedFile?.name || "document.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Processing PDF...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 2,
        }}
      >
        <PdfIcon sx={{ fontSize: 48, mb: 1, color: "error.main" }} />
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* PDF Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2">{totalPages} pages</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={handleDownload}
          size="small"
          title="Download PDF"
          disabled={currentDownloadLoading}
        >
          {currentDownloadLoading ? (
            <CircularProgress size={24} />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      </Box>

      {/* PDF Pages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          gap: 2,
        }}
      >
        {renderingPages ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Rendering pages...
            </Typography>
          </Box>
        ) : (
          renderedPages.map((pageData, index) => (
            <Box
              key={pageData.pageNum}
              sx={{
                position: "relative",
                border: "1px solid #dcdcdc",
                borderRadius: 0,
                mb: index === renderedPages.length - 1 ? 0 : 1,
              }}
            >
              <canvas
                width={pageData.width}
                height={pageData.height}
                ref={(el) => {
                  if (el && pageData.canvas) {
                    // Copy the rendered canvas to the display canvas
                    const ctx = el.getContext("2d");
                    if (!ctx) return;
                    ctx.clearRect(0, 0, pageData.width, pageData.height);
                    ctx.drawImage(pageData.canvas, 0, 0);
                  }
                }}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.7)",
                  color: "white",
                  px: 1,
                  py: 0.5,
                  borderRadius: 0,
                }}
              >
                Page {pageData.pageNum}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default PDFViewer;
